// src/components/resources/tools/generators/ReviewMockup/GoogleReview.tsx
'use client'

import { Star, UserRound, MoreVertical, ThumbsUp, Share2 } from 'lucide-react'
import { clsx } from 'clsx'
import Image from 'next/image'
import type { ReviewConfig } from './types'

/**
 * Google review mockup component.
 */
export function GoogleReview({ config }: { config: ReviewConfig }): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-400 to-purple-500">
          {config.avatar ? (
            <Image
              src={config.avatar}
              alt={`${config.reviewerName}'s avatar`}
              className="h-full w-full object-cover"
              width={40}
              height={40}
              unoptimized
              priority={false}
            />
          ) : (
            <UserRound className="h-5 w-5 text-white" aria-label="User avatar" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{config.reviewerName}</p>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>Local Guide</span>
            <span>&middot;</span>
            <span>24 reviews</span>
          </div>
        </div>
        <button className="p-1 hover:bg-gray-100 rounded-full" aria-label="More options">
          <MoreVertical className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Rating and Date */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center gap-0.5" aria-label={`Rating: ${config.rating} out of 5`}>
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={clsx(
                  'h-4 w-4',
                  i < config.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'
                )}
                aria-hidden="true"
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">{config.date}</span>
        </div>

        {/* Review Text */}
        <p className="text-sm text-gray-700 leading-relaxed">
          {config.reviewText}
        </p>

        {/* Actions */}
        <div className="mt-3 flex items-center gap-4">
          <button className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900" aria-label="Mark as helpful">
            <ThumbsUp className="h-4 w-4" />
            <span>Helpful</span>
          </button>
          <button className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900" aria-label="Share review">
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Business Response (Optional) */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-xs font-semibold">
            AS
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-900">{config.businessName}</span>
              <span className="text-xs text-gray-500">Owner</span>
            </div>
            <p className="text-sm text-gray-700">
              Thank you for the kind words! We&apos;re thrilled to hear our tools are helping your team ship faster.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
